import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { _money, _monthLabel } from './utils';
import { VarianceCell } from './VarianceCell';
import { EditableSiteRow } from './EditableSiteRow';

export function BudgetGrid({ title, testid, sectionLabel, rows, months, showLastYear, onDataChanged, collapsedGroups = {}, toggleGroup = () => {}, allowDiscontinue = false, onDiscontinueClient, dateRange, section2Toggles = {}, onToggleClientDiscontinue, previewUplift, pendingDiscontinueSiteIds, onToggleSiteDiscontinue, onDirtyChange }) {
  const isEmpty = rows.length === 0;
  const colCount = 11 + months.length + (showLastYear ? 2 : 0); // Edit + Client/Site + Last audit + Service + Frequency + Price-effective-from + Schedule-start + Last billed + £/job + Notes + months + Total(+LY+Var)

  // iter491 — Section subtotal row: sum every site's monthly values so
  // the grid has an inline running total that ties to the KPI ribbon.
  const monthTotals = React.useMemo(() => {
    const acc = {};
    months.forEach(m => { acc[m] = 0; });
    (rows || []).forEach(g => (g.sites || []).forEach(s => {
      months.forEach(m => { acc[m] += Number(s.months?.[m] || 0); });
    }));
    return acc;
  }, [rows, months]);
  const yearTotal = React.useMemo(() => (
    (rows || []).reduce((a, g) => a + Number(g.subtotal || 0), 0)
  ), [rows]);
  const lastYearTotal = React.useMemo(() => (
    (rows || []).reduce((a, g) => a + Number(g.subtotal_last_year || 0), 0)
  ), [rows]);
  const varianceTotal = yearTotal - lastYearTotal;
  return (
    <Card data-testid={testid}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-auto max-h-[calc(100vh-240px)] relative">
        <table className="w-full text-xs border-collapse min-w-[1200px]">
          <thead className="bg-slate-100 sticky top-0 z-30">
            <tr>
              {/* iter483 — inline edit actions moved to the LEFT.
                  Column stays empty on clean rows; shows Save/Discard/Master
                  buttons only while a row is being edited. */}
              <th className="px-1 py-2 border-b sticky left-0 bg-slate-100 z-40 w-24 min-w-[96px]"></th>
              <th className="text-left px-2 py-2 border-b border-r sticky left-24 bg-slate-100 z-40 min-w-[200px]">Client / Site</th>
              <th className="text-left px-2 py-2 border-b whitespace-nowrap">Last audit</th>
              <th className="text-left px-1 py-2 border-b min-w-[140px] w-[150px]">Service</th>
              <th className="text-left px-1 py-2 border-b min-w-[112px] w-[120px]">Frequency</th>
              <th className="text-left px-1 py-2 border-b min-w-[120px] w-[128px]">Price effective from</th>
              <th className="text-left px-1 py-2 border-b min-w-[120px] w-[128px]">Schedule start</th>
              <th className="text-right px-2 py-2 border-b whitespace-nowrap">Last billed</th>
              <th className="text-right px-1 py-2 border-b min-w-[76px] w-[76px]">£ / job</th>
              <th className="text-left px-1 py-2 border-b min-w-[130px] w-[150px]">Notes</th>
              {months.map(m => (
                <th key={m} className="text-right px-2 py-2 border-b whitespace-nowrap">{_monthLabel(m)}</th>
              ))}
              <th className="text-right px-2 py-2 border-b border-l bg-slate-50 whitespace-nowrap">Total</th>
              {showLastYear && <>
                <th className="text-right px-2 py-2 border-b whitespace-nowrap">Last year</th>
                <th className="text-right px-2 py-2 border-b whitespace-nowrap">Variance</th>
              </>}
            </tr>
          </thead>
          <tbody>
            {isEmpty && (
              <tr><td colSpan={colCount} className="text-center text-slate-500 py-8">
                No sites match the current filter.
              </td></tr>
            )}
            {rows.map(grp => {
              const gkey = `${testid}::${grp.client_id ?? grp.client_name}`;
              const collapsed = !!collapsedGroups[gkey];
              // iter484 — subtotal per month for the group so summary
              // rows show the same column info as the site rows.
              const monthSubs = {};
              months.forEach(m => {
                monthSubs[m] = (grp.sites || []).reduce((acc, s) => acc + (s.months?.[m] || 0), 0);
              });
              const toggledOff = !!section2Toggles[grp.client_id];
              return (
                <React.Fragment key={gkey}>
                  <tr className="bg-slate-50">
                    <td className="border-b sticky left-0 bg-slate-50 z-20">
                      {/* iter484 — Section 2 uses a TOGGLE (pending list) instead
                          of a per-row API call. Parent flushes with Save-All. */}
                      {allowDiscontinue && grp.client_id && (
                        <div className="px-1 py-1 flex justify-center">
                          <button
                            type="button"
                            onClick={() => onToggleClientDiscontinue && onToggleClientDiscontinue(grp.client_id)}
                            className={
                              'text-[10px] px-1.5 py-0.5 rounded border whitespace-nowrap font-semibold ' +
                              (toggledOff
                                ? 'bg-rose-600 text-white border-rose-600 hover:bg-rose-700'
                                : 'bg-white text-rose-700 border-rose-300 hover:bg-rose-50')
                            }
                            data-testid={`discontinue-toggle-${grp.client_id}`}
                            title={toggledOff ? 'Marked Inactive — click to undo' : 'Mark client as Inactive (Customer Master)'}
                          >
                            {toggledOff ? '✓ Inactive' : 'Set Inactive'}
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 font-semibold border-b sticky left-24 bg-slate-50 z-20">
                      <button
                        type="button"
                        onClick={() => toggleGroup(gkey)}
                        className="mr-2 text-slate-500 hover:text-slate-800 inline-flex items-center"
                        data-testid={`toggle-group-${testid}-${grp.client_id ?? 'unassigned'}`}
                        title={collapsed ? 'Expand' : 'Collapse'}
                      >
                        {collapsed ? '▶' : '▼'}
                      </button>
                      {grp.client_name}
                      <span className="ml-2 text-[11px] text-slate-500">({(grp.sites || []).length} site{(grp.sites || []).length === 1 ? '' : 's'})</span>
                    </td>
                  <td colSpan={8} className="border-b" />
                  {months.map(m => (
                      <td key={m} className={`px-2 py-1.5 text-right font-semibold border-b ${monthSubs[m] ? 'text-slate-700' : 'text-slate-300'}`}>
                        {monthSubs[m] ? _money(monthSubs[m]) : '—'}
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-right font-semibold border-l border-b">{_money(grp.subtotal)}</td>
                    {showLastYear && <>
                      <td className="px-2 py-1.5 text-right font-semibold border-b">{_money(grp.subtotal_last_year)}</td>
                      <td className="px-2 py-1.5 text-right font-semibold border-b"><VarianceCell variance={grp.subtotal_variance} /></td>
                    </>}
                  </tr>
                  {!collapsed && (grp.sites || []).map(s => (
                    <EditableSiteRow
                      key={s.site_id}
                      site={s}
                      months={months}
                      showLastYear={showLastYear}
                      onDataChanged={onDataChanged}
                      dateRange={dateRange}
                      previewUplift={previewUplift}
                      allowSiteDiscontinue={allowDiscontinue}
                      pendingDiscontinueSiteIds={pendingDiscontinueSiteIds}
                      onToggleSiteDiscontinue={onToggleSiteDiscontinue}
                      onDirtyChange={onDirtyChange}
                    />
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          {!isEmpty && (
            <tfoot className="bg-slate-100 border-t-2 border-slate-300 font-bold">
              <tr data-testid={`${testid}-subtotal-row`}>
                <td className="border-b sticky left-0 bg-slate-100 z-20" />
                <td className="px-2 py-2 border-b sticky left-24 bg-slate-100 z-20 whitespace-nowrap uppercase text-[11px] tracking-wide text-slate-700">
                  {sectionLabel || title} total
                </td>
                <td className="border-b" colSpan={8} />
                {months.map(m => (
                  <td key={m} className={`px-2 py-2 text-right border-b whitespace-nowrap ${monthTotals[m] ? '' : 'text-slate-400'}`}
                    data-testid={`${testid}-subtotal-${m}`}>
                    {monthTotals[m] ? _money(monthTotals[m]) : '—'}
                  </td>
                ))}
                <td className="px-2 py-2 text-right border-l border-b bg-slate-200 whitespace-nowrap"
                  data-testid={`${testid}-subtotal-year`}>{_money(yearTotal)}</td>
                {showLastYear && <>
                  <td className="px-2 py-2 text-right border-b whitespace-nowrap">{_money(lastYearTotal)}</td>
                  <td className="px-2 py-2 text-right border-b whitespace-nowrap"><VarianceCell variance={varianceTotal} /></td>
                </>}
              </tr>
            </tfoot>
          )}
        </table>
        </div>
      </CardContent>
    </Card>
  );
}
